import * as $ from 'jquery';

import 'etc/designs/base/clientlibs/shared/libraries/SCS.Url.js';

import { Disqus } from './services/Disqus';
import { Facebook } from './services/Facebook';
import { LinkedIn } from './services/LinkedIn';
import { Mail } from './services/Mail';
import { Twitter } from './services/Twitter';
import { WhatsApp } from './services/WhatsApp';
import { Xing } from './services/Xing';

export interface Count {
	count : number;
	fcount? : string;
	id? : string;
	error? : any;
}

export interface Counts extends Array<Count> {
	counts : number;
	fcounts : string;
	count : number;
	fcount : string;
}

export interface Service {
	name : string;
	category : string[];
	shareUrl(url : string, name? : string) : string;
	shareCount(url : string, name : string) : Promise<Count>;
}

interface ServiceRegistry {
	[name : string] : Service;
}

function currentPageUrl() {
	// Dereference object once
	const loc = document.location;
	// Build page url
	const canonical = $('link[rel=canonical]').attr('href');
	if(canonical) {
		return canonical;
	}

	return [
		loc.protocol,
		'//', loc.host,
		loc.pathname,
	].join('');
}

// Conversion factors/units for counts
const FACTOR = 1000;
const UNITS = ['k', 'm'];
const UNIT_PATTERN = /^(\d)([km])$/;

// Resolve `2k` to `2000`
export function decodeCount(count : string | number) {
	const parsedCount = String(count).toLowerCase();
	if (!UNIT_PATTERN.test(parsedCount)) {
		return parseInt(count as string, 10);
	}
	const f = UNITS.indexOf(RegExp.$2) + 1;

	return parseInt(RegExp.$1, 10) * Math.pow(FACTOR, f);
}

export function encodeCount(count : number) {
	let f = 0;

	while (count > FACTOR) {
		count /= FACTOR;
		f += 1;
	}

	count = Math.floor(count);
	let parsedCount = String(count);
	if (f) {
		parsedCount += UNITS[f - 1];
	}

	return parsedCount.toUpperCase();
}

export class SocialMedia {
	// Registered services
	private readonly services : ServiceRegistry;


	public constructor() {
		this.services = {};
	}

	public registerService(service : Service) {
		// Implement assertions to validate data
		// This always indicates a developer error
		if (!service.name) {
			throw(new Error('SocialMediaService has no name'));
		}
		if (!service.category) {
			throw(new Error('SocialMediaService has no category'));
		}
		// Register service on manager instance
		this.services[service.name] = service;
	}

	public shareUrl(name : string, url : string) {
		return this.dispatchCall('shareUrl', name, url);
	}

	public shareCount(names : string | ServiceRegistry | (string | Service)[], url : string) {
		url = SCS.Url.stripPathPrefixFromUrl(url);
		if(typeof names === 'string') {
			names = [names];
		}
		if(!Array.isArray(names)) {
			names = Object.keys(names);
		}

		const promises = names.map(name => {
			if(typeof name !== 'string') {
				name = name.name;
			}

			return this
				.dispatchCall('shareCount', name, url)
				.then(rv => {
					if (!rv.count) {
						rv.count = 0;
					}

					rv.count = decodeCount(rv.count);
					rv.fcount = encodeCount(rv.count);

					return rv;
				})
				.catch(e => {
					console.error(new Error('Error getting share count'), e);
					// Create fake response object with error
					const response : Count = {count: 0, fcount: '0', error: this};

					// Upgrade to a resolved promise
					return Promise.resolve(response);
				});
		});


		// Wait until all promises are resolved
		return Promise.all(promises).then(rv => {
			const aggregateCount = rv.reduce((count, item) => {
				return count + (item.count || 0);
			}, 0);
			const aggregateFCount = encodeCount(aggregateCount);

			const result : Counts = rv as Counts;
			Object.assign(result, {
				count: aggregateCount,
				counts: aggregateCount,
				fcount: aggregateFCount,
				fcounts: aggregateFCount,
			});

			return result;
		});
	}

	public servicesByCategory(category : string) {
		return Object
			.keys(this.services)
			.map(name => this.services[name])
			.filter(service => {
				return service.category.includes(category);
			});
	}

	public getRegisteredServices() {
		return {...this.services};
	}

	private dispatchCall(
		action : 'shareUrl',
		name : string,
		url : string,
	) : string;
	private dispatchCall(
		action : 'shareCount',
		name : string,
		url : string,
	) : Promise<Count>;
	private dispatchCall(
		action : 'shareUrl' | 'shareCount',
		name : string,
		url : string,
	) : string | Promise<Count> {
		// Get service from manager instance
		if(!(name in this.services)) {
			// Throw an error if service is not known
			// This always indicates a developer error
			throw(new Error('unknown social media service: ' + name));
		}
		const service = this.services[name];
		// Maybe use url of current page
		if (!url) {
			url = currentPageUrl();
		}

		const fn = (service[action]) as (...args : any) => string | Promise<Count>;

		// Dispatch to the given action
		return fn.call(this, url, name);
	}
}

let singleton : SocialMedia | undefined;

export function getController() {
	if(!singleton) {
		singleton = new SocialMedia();
		singleton.registerService(new Disqus());
		singleton.registerService(new Facebook());
		singleton.registerService(new LinkedIn());
		singleton.registerService(new Mail());
		singleton.registerService(new Twitter());
		singleton.registerService(new WhatsApp());
		singleton.registerService(new Xing());
	}

	return singleton;
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/generic/socialmedia/SocialMedia.ts